import Fuse from 'fuse.js';
import { Link } from 'react-router-dom';
import { BlogMeta } from '../../types/blog.model';

interface Props {
  wrongSlug: string;
  blog: BlogMeta;
}

/**
 * Shown in the post content area when a post slug doesn't resolve.
 *
 * - Fuzzy-matches the wrong slug against the blog's post list using Fuse.js.
 * - Desktop: if matches exist → show suggestion cards. If none → guide user
 *   to the sidebar chapter list (already visible).
 * - Mobile: sidebar is hidden, so always show inline chapter list as fallback.
 */
export default function PostNotFound({ wrongSlug, blog }: Props) {
  const fuse = new Fuse(blog.posts, {
    keys: ['title', 'slug'],
    threshold: 0.45,
    includeScore: true,
  });

  const suggestions = fuse
    .search(wrongSlug)
    .filter((r) => (r.score ?? 1) < 0.5)
    .slice(0, 3)
    .map((r) => r.item);

  return (
    <div className="flex flex-col items-center text-center py-16 px-4 max-w-xl mx-auto">
      {/* Icon */}
      <div className="text-5xl mb-5 select-none">🔍</div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">Post not found</h2>

      <p className="text-gray-500 mb-1">
        We couldn&apos;t find a post matching
      </p>
      <code className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded font-mono mb-6">
        {wrongSlug}
      </code>

      {suggestions.length > 0 ? (
        <>
          <p className="font-semibold text-gray-700 mb-3">
            Did you mean?
          </p>
          <div className="space-y-2 w-full text-left">
            {suggestions.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${blog.slug}/${post.slug}`}
                className="flex flex-col border border-gray-200 dark:border-outline-variant/20 rounded-lg px-4 py-3 hover:bg-gray-50 dark:hover:bg-surface-container transition-colors"
              >
                <span className="font-medium text-gray-900">{post.title}</span>
                <span className="text-xs text-gray-400 font-mono mt-0.5">
                  {post.slug}
                </span>
              </Link>
            ))}
          </div>
          <Link
            to={`/blog/${blog.slug}/${blog.posts[0]?.slug}`}
            className="mt-6 text-sm text-primary hover:underline"
          >
            Start from the beginning →
          </Link>
        </>
      ) : (
        <>
          {/* Desktop: sidebar chapter list is already visible on the left */}
          <p className="hidden md:block text-gray-500 mt-2">
            The URL might be a typo. Use the{' '}
            <span className="font-semibold text-gray-700">chapter list on the left</span> to
            find the post you&apos;re looking for.
          </p>

          {/* Mobile: sidebar is hidden — show inline chapter list */}
          <div className="md:hidden mt-4 w-full text-left">
            <p className="text-gray-500 mb-3">
              Browse all chapters in this series:
            </p>
            <div className="space-y-2">
              {blog.posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${blog.slug}/${post.slug}`}
                  className="flex flex-col border border-gray-200 dark:border-outline-variant/20 rounded-lg px-4 py-3 hover:bg-gray-50 dark:hover:bg-surface-container transition-colors"
                >
                  <span className="font-medium text-gray-900">{post.title}</span>
                  <span className="text-xs text-gray-400 font-mono mt-0.5">
                    {post.slug}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
